import React, { useMemo, useState } from 'react';
import {
  CAMPAIGN_SANDBOX_DEMO_PERSONAS,
  SANDBOX_DEMO_PRESET_CATEGORIES,
} from '../../../../campaignSandboxSeedBuilders';
import { TEST_PRESET_CATEGORY_OPTIONS } from './forge-v2/constants';

const normalizeCategories = (value) => {
  const source = value && typeof value === 'object' ? value : SANDBOX_DEMO_PRESET_CATEGORIES;
  return Object.fromEntries(
    TEST_PRESET_CATEGORY_OPTIONS.map((option) => [
      option.key,
      Object.prototype.hasOwnProperty.call(source, option.key)
        ? source[option.key] === true
        : SANDBOX_DEMO_PRESET_CATEGORIES[option.key] === true,
    ]),
  );
};

const fieldStyle = (theme) => ({
  width: '100%',
  background: 'rgba(0,0,0,0.32)',
  border: `1px solid ${theme.colors.borderLight}`,
  color: theme.colors.textPrimary,
  borderRadius: theme.effects?.borderRadiusSm || '4px',
  padding: '9px 10px',
  fontSize: '0.88rem',
  fontFamily: theme.typography?.fontFamily || 'serif',
  boxSizing: 'border-box',
});

const buttonStyle = (theme, active = false) => ({
  padding: '8px 12px',
  background: active ? theme.colors.accent : 'transparent',
  color: active ? theme.colors.textOnAccent : theme.colors.textSecondary,
  border: `1px solid ${active ? theme.colors.accent : theme.colors.borderLight}`,
  borderRadius: 4,
  cursor: 'pointer',
  fontFamily: theme.typography?.fontFamily || 'serif',
  fontSize: '0.82rem',
});

export const SandboxPartyModal = ({
  theme,
  chars = {},
  activeCharId = null,
  isDmView = true,
  presetCategories = SANDBOX_DEMO_PRESET_CATEGORIES,
  onClose,
  onEnterDm,
  onEnterPersona,
  onAddDemoPersona,
  onAddBlankPersona,
  onPresetCategoriesChange,
}) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const [blankName, setBlankName] = useState('');
  const [categoryDraft, setCategoryDraft] = useState(() => normalizeCategories(presetCategories));

  const personas = useMemo(() => (
    Object.values(chars || {})
      .filter((character) => character?.isSandboxCharacter === true || character?.campaignCharacterKind === 'sandbox')
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
  ), [chars]);

  const existingPresetKeys = useMemo(() => new Set(
    personas.map((persona) => persona?.sandboxPresetKey).filter(Boolean),
  ), [personas]);

  const toggleCategory = (key) => {
    setCategoryDraft((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const saveCategories = () => {
    onPresetCategoriesChange && onPresetCategoriesChange(categoryDraft);
  };

  const createBlank = () => {
    const name = blankName.trim();
    if (!name) return;
    onAddBlankPersona && onAddBlankPersona({ name });
    setBlankName('');
  };

  return (
    <div
      role="dialog"
      aria-label="Sandbox party"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.72)',
        zIndex: 212,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 760,
          maxHeight: 'calc(100vh - 48px)',
          overflowY: 'auto',
          background: c.bgCardSolid,
          border: `1px solid ${c.borderPrimary}`,
          borderRadius: theme.effects?.borderRadiusLg || '10px',
          padding: 24,
          boxShadow: '0 18px 48px rgba(0,0,0,0.62)',
          color: c.textPrimary,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <h2 style={{ margin: 0, fontFamily: t.headingFamily, color: c.accent, fontSize: '1.35rem' }}>
              View As
            </h2>
            <div style={{ marginTop: 4, color: c.textMuted, fontSize: '0.82rem' }}>
              Switch between the DM dashboard and local sandbox test characters.
            </div>
          </div>
          <button type="button" onClick={onClose} style={buttonStyle(theme)}>Close</button>
        </div>

        <section style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 10 }}>
            <h3 style={{ margin: 0, color: c.textPrimary, fontSize: '1rem', fontFamily: t.headingFamily }}>
              Switch View
            </h3>
            <button type="button" onClick={onEnterDm} style={buttonStyle(theme, isDmView)}>
              Enter as DM
            </button>
          </div>
          {personas.length === 0 ? (
            <div style={{ color: c.textMuted, border: `1px solid ${c.borderLight}`, borderRadius: 4, padding: 12 }}>
              No test characters yet.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 }}>
              {personas.map((persona) => (
                <div
                  key={persona.id}
                  style={{
                    border: `1px solid ${persona.id === activeCharId ? c.accent : c.borderLight}`,
                    borderRadius: 4,
                    padding: 12,
                    background: persona.id === activeCharId ? 'rgba(201,162,39,0.12)' : 'rgba(0,0,0,0.16)',
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>{persona.name || 'Test Character'}</div>
                  <div style={{ color: c.textMuted, fontSize: '0.78rem', marginBottom: 10 }}>
                    {persona.race || 'Unknown'} {persona.charClass ? `- ${persona.charClass}` : ''}
                  </div>
                  <button type="button" onClick={() => onEnterPersona && onEnterPersona(persona.id)} style={buttonStyle(theme, persona.id === activeCharId && !isDmView)}>
                    Enter
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        <section style={{ marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 10px', color: c.textPrimary, fontSize: '1rem', fontFamily: t.headingFamily }}>
            Demo Test Characters
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
            {CAMPAIGN_SANDBOX_DEMO_PERSONAS.map((persona) => {
              const exists = existingPresetKeys.has(persona.presetKey);
              return (
                <button
                  key={persona.presetKey}
                  type="button"
                  onClick={() => onAddDemoPersona && onAddDemoPersona(persona.presetKey)}
                  style={buttonStyle(theme)}
                >
                  {exists ? `Reset ${persona.name}` : `Add ${persona.name}`}
                </button>
              );
            })}
          </div>
        </section>

        <section style={{ marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 10px', color: c.textPrimary, fontSize: '1rem', fontFamily: t.headingFamily }}>
            Blank Test Character
          </h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              aria-label="Blank test character name"
              type="text"
              value={blankName}
              onChange={(event) => setBlankName(event.target.value)}
              placeholder="Name"
              style={{ ...fieldStyle(theme), flex: '1 1 220px' }}
            />
            <button type="button" onClick={createBlank} disabled={!blankName.trim()} style={buttonStyle(theme)}>
              Add blank
            </button>
          </div>
        </section>

        <section>
          <h3 style={{ margin: '0 0 10px', color: c.textPrimary, fontSize: '1rem', fontFamily: t.headingFamily }}>
            Preset Categories
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))', gap: 8 }}>
            {TEST_PRESET_CATEGORY_OPTIONS.map((option) => (
              <label
                key={option.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 10px',
                  border: `1px solid ${c.borderLight}`,
                  borderRadius: 4,
                  color: c.textSecondary,
                  fontSize: '0.82rem',
                }}
              >
                <input
                  type="checkbox"
                  checked={categoryDraft[option.key] === true}
                  onChange={() => toggleCategory(option.key)}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <button type="button" onClick={saveCategories} style={buttonStyle(theme, true)}>
              Apply to test characters
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};
